import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Configuración de axios para admin
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación y validar token
adminApi.interceptors.response.use(
  async (response) => {
    // Validar token en cada respuesta exitosa para detectar expiración temprana
    const token = localStorage.getItem('adminToken');
    if (token && response.config.url !== '/auth/verify') {
      try {
        // Verificar token de forma silenciosa
        await adminApi.post('/auth/verify', { token });
      } catch (error) {
        // Token inválido, limpiar y redirigir
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
          window.location.href = '/admin';
        }
      }
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      // Redirigir solo si no estamos ya en la página de admin
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

export const adminService = {
  // Verificar token de administrador
  async verifyToken(token) {
    try {
      const response = await adminApi.post('/auth/verify', { token });
      return response.data;
    } catch (error) {
      console.error('Error verificando token:', error);
      throw error;
    }
  },

  // Login de administrador
  async login(credentials) {
    try {
      const response = await adminApi.post('/users/login', credentials);
      return response.data;
    } catch (error) {
      // Procesar el error antes de re-lanzarlo
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Error al iniciar sesión';
      
      // Crear un error más estructurado
      const processedError = new Error(errorMessage);
      processedError.response = error.response;
      processedError.status = error.response?.status;
      
      throw processedError;
    }
  },

  // Logout de administrador
  async logout() {
    try {
      await adminApi.post('/users/logout');
    } catch (error) {
      console.error('Error en logout de admin:', error);
      // No lanzar error aquí, siempre limpiar localStorage
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
    }
  },

  // Obtener lista de reservas
  async getReservations() {
    try {
      const response = await adminApi.get('/admin/reservations');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo reservas:', error);
      throw error;
    }
  },

  // Obtener lista de usuarios
  async getUsers() {
    try {
      const response = await adminApi.get('/users');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo usuarios:', error);
      throw error;
    }
  },

  // Obtener lista de Alumnos
  async getStudents() {
    try {
      const response = await adminApi.get('/students');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo Alumnos:', error);
      throw error;
    }
  },

  // Obtener lista de eventos
  async getEvents() {
    try {
      const response = await adminApi.get('/events');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo eventos:', error);
      throw error;
    }
  },

  // Obtener lista de códigos de acceso
  async getAccessCodes() {
    try {
      const response = await adminApi.get('/access-codes');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo códigos de acceso:', error);
      throw error;
    }
  },

  // Cambiar la fecha de vencimiento de un código de acceso (solo administradores)
  async updateAccessCodeExpiry(accessCodeId, expiresAt) {
    try {
      const response = await adminApi.patch(`/access-codes/${accessCodeId}/expiry`, { expiresAt });
      return response.data;
    } catch (error) {
      console.error('Error actualizando fecha de vencimiento:', error);
      throw error;
    }
  },

  // Obtener estadísticas de reservas para un evento
  async getEventReservationStats(eventId) {
    try {
      const response = await adminApi.get(`/reservations/event/${eventId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estadísticas de reservas:', error);
      throw error;
    }
  },

  // Obtener reservas por evento
  async getReservationsByEvent(eventId) {
    try {
      const response = await adminApi.get(`/reservations?eventId=${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo reservas del evento:', error);
      throw error;
    }
  },

  // Obtener detalles de un evento específico
  async getEventById(eventId) {
    try {
      const response = await adminApi.get(`/events/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo detalles del evento:', error);
      throw error;
    }
  },
};

export default adminService; 